// components/Navigation/UKEntitySelector/UKEntitySelector.js
import React, { useMemo, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight, faLock, faMinusCircle } from '@fortawesome/free-solid-svg-icons';
import { Form } from 'react-bootstrap';
import { sortBy } from 'lodash';
import {
    toggleNetwork,
    toggleChain,
    selectNetwork,
    selectChain,
    toggleClub,
    deselectNetwork,
    deselectChain,
    clearAllSelections
} from '../../../../redux/slices/myGymTV/ukEntitiesSlice';
import QuickSelectClubs from '../QuickSelectClubs/QuickSelectClubs';
import './uk-entity-selector.scss';

const UKEntitySelector = ({
    standalone = true,
    initialSelectedClubIds = [],
    onSelectionChange = null,
    allVenues = null
}) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const { ukEntityData: reduxUkEntityData, user } = useSelector((state) => state.user);
    const {
        selectedClubs: reduxSelectedClubs,
        expandedNetworks: reduxExpandedNetworks,
        expandedChains: reduxExpandedChains
    } = useSelector((state) => state.ukEntities);

    // Check if current user is developer (UserID = 6)
    const isDeveloper = user?.UserID === 6;

    // Lock selector when not on dashboard (only in standalone mode)
    const isOnDashboard = location.pathname === '/' || location.pathname === '/dashboard' || location.pathname === '/content-overview';
    const isLocked = standalone && !isOnDashboard;

    // Local state for controlled mode
    const [localSelectedClubs, setLocalSelectedClubs] = useState([]);
    const [localExpandedNetworks, setLocalExpandedNetworks] = useState([]);
    const [localExpandedChains, setLocalExpandedChains] = useState([]);

    // Developer filter state
    const [countryFilter, setCountryFilter] = useState('UK'); // 'US', 'UK', or 'BOTH'

    // Use either Redux state (standalone) or local state (controlled)
    const ukEntityDataRaw = allVenues || reduxUkEntityData;
    const selectedClubs = standalone ? reduxSelectedClubs : localSelectedClubs;
    const expandedNetworks = standalone ? reduxExpandedNetworks : localExpandedNetworks;
    const expandedChains = standalone ? reduxExpandedChains : localExpandedChains;

    // Filter entity data by country (dev feature only)
    const ukEntityData = useMemo(() => {
        if (!ukEntityDataRaw) return [];

        // Only apply filter for developers
        if (!isDeveloper) {
            return ukEntityDataRaw;
        }

        if (countryFilter === 'BOTH') {
            return ukEntityDataRaw;
        } else if (countryFilter === 'UK') {
            return ukEntityDataRaw.filter(club => club.CountryId === 2);
        } else if (countryFilter === 'US') {
            return ukEntityDataRaw.filter(club => club.CountryId !== 2);
        }
        return ukEntityDataRaw;
    }, [ukEntityDataRaw, countryFilter, isDeveloper]);

    // Initialize local state in controlled mode
    useEffect(() => {
        if (!standalone && initialSelectedClubIds.length > 0) {
            const initialClubs = ukEntityData.filter(venue =>
                initialSelectedClubIds.includes(venue.ClubId)
            );
            setLocalSelectedClubs(initialClubs);
        }
    }, [standalone, initialSelectedClubIds, ukEntityData]);

    // Notify parent of selection changes in controlled mode
    useEffect(() => {
        if (!standalone && onSelectionChange) {
            onSelectionChange(localSelectedClubs.map(c => c.ClubId));
        }
    }, [localSelectedClubs, standalone, onSelectionChange]);

    // Build hierarchical structure from flat data
    const hierarchicalData = useMemo(() => {
        if (!ukEntityData || ukEntityData.length === 0) return [];

        const networks = {};
      
        ukEntityData.forEach((entity) => {
            if (!networks[entity.NetworkId]) {
                networks[entity.NetworkId] = {
                    NetworkId: entity.NetworkId,
                    NetworkName: entity.NetworkName,
                    chains: {}
                };
            }

            if (!networks[entity.NetworkId].chains[entity.ChainId]) {
                networks[entity.NetworkId].chains[entity.ChainId] = {
                    ChainId: entity.ChainId,
                    ChainName: entity.ChainName,
                    NetworkId: entity.NetworkId,
                    clubs: []
                };
            }

            networks[entity.NetworkId].chains[entity.ChainId].clubs.push(entity);
        });

        // if a network has more than a single chain, sort network chains alphabetically 
        Object.values(networks).forEach((network) => {
            if (Object.values(network.chains)?.length > 1) {
                network.chains = sortBy(Object.values(network.chains), 'ChainName');
            }
        });

        // order network names alphabetically in return
        return Object.values(sortBy(networks, 'NetworkName'));
    }, [ukEntityData]);

    // Helper functions to check selection state
    const getNetworkSelectionState = (networkId) => {
        const networkClubs = ukEntityData.filter(c => c.NetworkId === networkId);
        const selectedCount = networkClubs.filter(club =>
            selectedClubs.some(sc => sc.ClubId === club.ClubId)
        ).length;

        if (selectedCount === 0) return 'none';
        if (selectedCount === networkClubs.length) return 'all';
        return 'some';
    };

    const getChainSelectionState = (chainId) => {
        const chainClubs = ukEntityData.filter(c => c.ChainId === chainId);
        const selectedCount = chainClubs.filter(club =>
            selectedClubs.some(sc => sc.ClubId === club.ClubId)
        ).length;

        if (selectedCount === 0) return 'none';
        if (selectedCount === chainClubs.length) return 'all';
        return 'some';
    };

    const isClubSelected = (clubId) => {
        return selectedClubs.some(c => c.ClubId === clubId);
    };

    const handleNetworkClick = (network) => {
        if (isLocked) return;

        if (standalone) {
            dispatch(toggleNetwork({ networkId: network.NetworkId }));
        } else {
            setLocalExpandedNetworks(prev =>
                prev.includes(network.NetworkId)
                    ? prev.filter(id => id !== network.NetworkId)
                    : [...prev, network.NetworkId]
            );
        }
    };

    const handleNetworkCheckbox = (network, e) => {
        e.stopPropagation();
        if (isLocked) return;

        const selectionState = getNetworkSelectionState(network.NetworkId);

        if (standalone) {
            if (selectionState === 'all') {
                dispatch(deselectNetwork(network.NetworkId));
            } else {
                dispatch(selectNetwork({
                    networkId: network.NetworkId,
                    allClubs: ukEntityData
                }));
            }
        } else {
            const networkClubs = ukEntityData.filter(c => c.NetworkId === network.NetworkId);
            if (selectionState === 'all') {
                setLocalSelectedClubs(prev =>
                    prev.filter(club => club.NetworkId !== network.NetworkId)
                );
            } else {
                setLocalSelectedClubs(prev => {
                    const filtered = prev.filter(club => club.NetworkId !== network.NetworkId);
                    return [...filtered, ...networkClubs];
                });
            }
        }
    };

    const handleChainClick = (chain) => {
        if (isLocked) return;

        if (standalone) {
            dispatch(toggleChain({ chainId: chain.ChainId }));
        } else {
            setLocalExpandedChains(prev =>
                prev.includes(chain.ChainId)
                    ? prev.filter(id => id !== chain.ChainId)
                    : [...prev, chain.ChainId]
            );
        }
    };

    const handleChainCheckbox = (chain, e) => {
        e.stopPropagation();
        if (isLocked) return;

        const selectionState = getChainSelectionState(chain.ChainId);

        if (standalone) {
            if (selectionState === 'all') {
                dispatch(deselectChain(chain.ChainId));
            } else {
                dispatch(selectChain({
                    chainId: chain.ChainId,
                    allClubs: ukEntityData
                }));
            }
        } else {
            const chainClubs = ukEntityData.filter(c => c.ChainId === chain.ChainId);
            if (selectionState === 'all') {
                setLocalSelectedClubs(prev =>
                    prev.filter(club => club.ChainId !== chain.ChainId)
                );
            } else {
                setLocalSelectedClubs(prev => {
                    const filtered = prev.filter(club => club.ChainId !== chain.ChainId);
                    return [...filtered, ...chainClubs];
                });
            }
        }
    };

    const handleClubCheckbox = (club, e) => {
        e.stopPropagation();
        if (isLocked) return;

        if (standalone) {
            dispatch(toggleClub(club));
        } else {
            const isSelected = localSelectedClubs.some(c => c.ClubId === club.ClubId);
            if (isSelected) {
                setLocalSelectedClubs(prev => prev.filter(c => c.ClubId !== club.ClubId));
            } else {
                setLocalSelectedClubs(prev => [...prev, club]);
            }
        }
    };

    if (!ukEntityDataRaw || ukEntityDataRaw.length === 0) {
        return null;
    }

    const clearSelection = () => {
        if (isLocked) return;

        if (standalone) {
            dispatch(clearAllSelections());
        } else {
            setLocalSelectedClubs([]);
            setLocalExpandedNetworks([]);
            setLocalExpandedChains([]);
        }
    };

    return (
        <div className={`uk-entity-selector mb-4 ${isLocked ? 'locked' : ''}`}>
            {standalone && (
                <div className="selector-header mb-2">
                    <h6 className="text-white mb-0">
                        {isLocked && <FontAwesomeIcon
className="me-2"
icon={faLock}
size="sm"
                                     />}
                        Select Locations
                    </h6>
                    {isLocked && (
                        <small className="text-warning d-block mt-1">
                            Return to Dashboard to change selection
                        </small>
                    )}
                </div>
            )}

            {/* Developer Country Filter - Only for UserID 6 */}
            {standalone && isDeveloper && !isLocked && (
                <div className="dev-filter mb-2">
                    <Form.Select
                        onChange={(e) => setCountryFilter(e.target.value)}
                        size="sm"
                        style={{
                            backgroundColor: '#2a2a2a',
                            color: '#ffc107',
                            border: '1px solid #ffc107',
                            fontSize: '0.75rem'
                        }}
                        value={countryFilter}
                    >
                        <option value="UK">🇬🇧 UK Only</option>
                        <option value="US">🇺🇸 US Only</option>
                        <option value="BOTH">🌐 Both (Dev)</option>
                    </Form.Select>
                </div>
            )}

            {standalone && !isLocked && <QuickSelectClubs />}

            {!standalone && (
                <div className="mb-3">
                    <strong>Selected: {selectedClubs.length} venues</strong>
                </div>
            )}
            {(selectedClubs.length > 0 && !isLocked) && <button
                type="button"
                className="clear-selection-btn"
                onClick={clearSelection}
                title="Clear Selection"
                >
                Clear Selection
                <FontAwesomeIcon className="clear-selection-btn-icon" icon={faMinusCircle} />
            </button>}
            <div className={`entity-tree ${isLocked ? 'disabled' : ''}`}>
                {hierarchicalData.map((network) => {
                    const networkState = getNetworkSelectionState(network.NetworkId);

                    return (
                        <div
                            className="network-item"
                            key={network.NetworkId}
                        >
                            <div
                                className={`entity-row network-row ${isLocked ? 'locked' : ''}`}
                                onClick={() => handleNetworkClick(network)}
                            >
                                <IndeterminateCheckbox
                                    checked={networkState === 'all'}
                                    disabled={isLocked}
                                    indeterminate={networkState === 'some'}
                                    onChange={(e) => handleNetworkCheckbox(network, e)}
                                    onClick={(e) => e.stopPropagation()}
                                />
                                <FontAwesomeIcon
                                    className="expand-icon"
                                    icon={expandedNetworks.includes(network.NetworkId) ? faChevronDown : faChevronRight}
                                />
                                <span className="entity-name">{network.NetworkName}</span>
                            </div>

                            {expandedNetworks.includes(network.NetworkId) && (
                                <div className="chains-container">
                                    {Object.values(network.chains).map((chain) => {
                                        const chainState = getChainSelectionState(chain.ChainId);

                                        return (
                                            <div
                                                className="chain-item"
                                                key={chain.ChainId}
                                            >
                                                <div
                                                    className={`entity-row chain-row ${isLocked ? 'locked' : ''}`}
                                                    onClick={() => handleChainClick(chain)}
                                                >
                                                    <IndeterminateCheckbox
                                                        checked={chainState === 'all'}
                                                        disabled={isLocked}
                                                        indeterminate={chainState === 'some'}
                                                        onChange={(e) => handleChainCheckbox(chain, e)}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                    <FontAwesomeIcon
                                                        className="expand-icon"
                                                        icon={expandedChains.includes(chain.ChainId) ? faChevronDown : faChevronRight}
                                                    />
                                                    <span className="entity-name">{chain.ChainName}</span>
                                                </div>

                                                {expandedChains.includes(chain.ChainId) && (
                                                    <div className="clubs-container">
                                                        {chain.clubs.map((club) => (
                                                            <div
                                                                className="club-item"
                                                                key={club.ClubId}
                                                            >
                                                                <div className={`entity-row club-row ${isLocked ? 'locked' : ''}`}>
                                                                    <input
                                                                        checked={isClubSelected(club.ClubId)}
                                                                        disabled={isLocked}
                                                                        onChange={(e) => handleClubCheckbox(club, e)}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        type="checkbox"
                                                                    />
                                                                    <span className="entity-name">{club.ClubName}</span>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// Separate component for indeterminate checkbox
const IndeterminateCheckbox = ({ indeterminate, ...rest }) => {
    const ref = useRef(null);

    useEffect(() => {
        if (ref.current) {
            ref.current.indeterminate = indeterminate;
        }
    }, [indeterminate]);

    return <input
        ref={ref}
        type="checkbox"
        {...rest}
           />;
};

export default UKEntitySelector;